/**
 * Service pour gérer l'accès à la bibliothèque de photos
 * Compatible avec Capacitor (pour les apps natives)
 */

import { Capacitor } from '@capacitor/core';
import { Camera, CameraSource, CameraResultType, CameraPermissionState } from '@capacitor/camera';

/**
 * Résultat d'une opération photo réussie
 */
export interface PhotoSuccess {
  success: true;
  photo: string;
}

/**
 * Résultat d'une opération photo échouée
 */
export interface PhotoFailure {
  success: false;
  cancelled: boolean;
  error?: string;
}

export type PhotoResult = PhotoSuccess | PhotoFailure;

/**
 * Détecte si une erreur est une annulation utilisateur
 */
const isUserCancellation = (err: any): boolean => {
  if (!err) return false;
  const message = (err.message || err.toString() || '').toLowerCase();
  return (
    message.includes('cancel') ||
    message.includes('user cancelled') ||
    message.includes('user denied') ||
    message.includes('dismissed') ||
    message.includes('no image picked') ||
    message.includes('user rejected') ||
    message.includes('photo picker was dismissed')
  );
};

/**
 * Détecte si une erreur est un refus de permission
 */
const isPermissionDenied = (err: any): boolean => {
  if (!err) return false;
  const message = (err.message || err.toString() || '').toLowerCase();
  return (
    message.includes('permission') ||
    message.includes('denied') ||
    message.includes('not authorized') ||
    message.includes('access denied') ||
    message.includes('restricted')
  );
};

/**
 * Log détaillé pour le debugging natif
 */
const logDebug = (prefix: string, ...args: any[]) => {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] ${prefix}`, ...args);
};

/**
 * Vérifie si les permissions sont suffisantes pour l'opération
 */
const hasRequiredPermission = (state: CameraPermissionState): boolean => {
  return state === 'granted' || state === 'limited';
};

/**
 * Vérifie les permissions pour accéder à la bibliothèque de photos
 */
export const checkPhotoLibraryPermissions = async (): Promise<boolean> => {
  try {
    const platform = Capacitor.getPlatform();
    logDebug('📱 [checkPermissions]', `Platform: ${platform}`);
    
    if (!Capacitor.isNativePlatform()) {
      logDebug('📱 [checkPermissions]', 'Web environment - no permission check needed');
      return true;
    }

    const permissions = await Camera.checkPermissions();
    logDebug('📸 [checkPermissions]', 'Current permissions:', JSON.stringify(permissions));
    
    return hasRequiredPermission(permissions.photos);
  } catch (err) {
    logDebug('❌ [checkPermissions]', 'Error checking permissions:', err);
    return false;
  }
};

/**
 * Vérifie les permissions pour la caméra
 */
export const checkCameraPermissions = async (): Promise<boolean> => {
  try {
    const platform = Capacitor.getPlatform();
    logDebug('📱 [checkCameraPermissions]', `Platform: ${platform}`);
    
    if (!Capacitor.isNativePlatform()) {
      logDebug('📱 [checkCameraPermissions]', 'Web environment - no permission check needed');
      return true;
    }

    const permissions = await Camera.checkPermissions();
    logDebug('📸 [checkCameraPermissions]', 'Current permissions:', JSON.stringify(permissions));
    
    return hasRequiredPermission(permissions.camera);
  } catch (err) {
    logDebug('❌ [checkCameraPermissions]', 'Error checking camera permissions:', err);
    return false;
  }
};

/**
 * Demande les permissions pour accéder à la bibliothèque de photos
 */
export const requestPhotoLibraryPermissions = async (): Promise<boolean> => {
  try {
    const platform = Capacitor.getPlatform();
    logDebug('📱 [requestPermissions]', `Platform: ${platform}`);
    
    if (!Capacitor.isNativePlatform()) {
      logDebug('📱 [requestPermissions]', 'Web environment - no permission request needed');
      return true;
    }

    logDebug('📸 [requestPermissions]', 'Requesting photos permission...');
    const permissions = await Camera.requestPermissions({ permissions: ['photos'] });
    logDebug('📸 [requestPermissions]', 'Permission result:', JSON.stringify(permissions));
    
    return hasRequiredPermission(permissions.photos);
  } catch (err) {
    logDebug('❌ [requestPermissions]', 'Error requesting permissions:', err);
    return false;
  }
};

/**
 * Demande les permissions pour la caméra
 */
export const requestCameraPermissions = async (): Promise<boolean> => {
  try {
    const platform = Capacitor.getPlatform();
    logDebug('📱 [requestCameraPermissions]', `Platform: ${platform}`);
    
    if (!Capacitor.isNativePlatform()) {
      logDebug('📱 [requestCameraPermissions]', 'Web environment - no permission request needed');
      return true;
    }

    logDebug('📸 [requestCameraPermissions]', 'Requesting camera permission...');
    const permissions = await Camera.requestPermissions({ permissions: ['camera'] });
    logDebug('📸 [requestCameraPermissions]', 'Permission result:', JSON.stringify(permissions));
    
    return hasRequiredPermission(permissions.camera);
  } catch (err) {
    logDebug('❌ [requestCameraPermissions]', 'Error requesting camera permissions:', err);
    return false;
  }
};

/**
 * Sélectionne une photo depuis la bibliothèque
 * @param options - Options pour la sélection de photo
 * @returns PhotoResult avec le résultat de l'opération
 */
export const pickPhotoFromLibrary = async (options?: {
  quality?: number;
  allowEditing?: boolean;
  width?: number;
  height?: number;
}): Promise<PhotoResult> => {
  const platform = Capacitor.getPlatform();
  logDebug('📸 [pickPhoto]', `Starting photo picker on platform: ${platform}`);
  
  try {
    if (Capacitor.isNativePlatform()) {
      // Vérifier et demander les permissions d'abord
      logDebug('📸 [pickPhoto]', 'Checking photo library permissions...');
      const hasPermission = await checkPhotoLibraryPermissions();
      
      if (!hasPermission) {
        logDebug('📸 [pickPhoto]', 'Permission not granted, requesting...');
        const granted = await requestPhotoLibraryPermissions();
        
        if (!granted) {
          logDebug('❌ [pickPhoto]', 'Permission denied by user');
          return { 
            success: false, 
            cancelled: false, 
            error: 'Permission refusée. Veuillez autoriser l\'accès aux photos dans les réglages.' 
          };
        }
      }
      
      logDebug('📸 [pickPhoto]', 'Opening native photo picker...', {
        quality: options?.quality || 90,
        allowEditing: options?.allowEditing || false,
        width: options?.width,
        height: options?.height,
      });
      
      const photo = await Camera.getPhoto({
        quality: options?.quality || 90,
        allowEditing: options?.allowEditing || false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
        width: options?.width,
        height: options?.height,
        correctOrientation: true,
      });

      logDebug('📸 [pickPhoto]', 'Photo received:', {
        format: photo.format,
        hasDataUrl: !!photo.dataUrl,
        hasWebPath: !!photo.webPath,
        dataUrlLength: photo.dataUrl?.length,
      });

      const photoUrl = photo.dataUrl || photo.webPath || null;
      if (photoUrl) {
        logDebug('✅ [pickPhoto]', `Photo selected successfully, format: ${photo.format}`);
        return { success: true, photo: photoUrl };
      } else {
        logDebug('⚠️ [pickPhoto]', 'No photo URL returned');
        return { success: false, cancelled: true };
      }
    } else {
      // Fallback pour le web
      logDebug('📸 [pickPhoto]', 'Opening web file picker...');
      return await pickPhotoFromLibraryWeb();
    }
  } catch (err: any) {
    logDebug('📸 [pickPhoto]', 'Error or cancellation:', {
      message: err?.message,
      code: err?.code,
      stack: err?.stack,
    });
    
    if (isUserCancellation(err)) {
      logDebug('📸 [pickPhoto]', 'Operation cancelled by user');
      return { success: false, cancelled: true };
    }
    
    if (isPermissionDenied(err)) {
      logDebug('❌ [pickPhoto]', 'Permission denied for gallery');
      return { 
        success: false, 
        cancelled: false, 
        error: 'Permission refusée. Veuillez autoriser l\'accès aux photos dans les réglages.' 
      };
    }
    
    logDebug('❌ [pickPhoto]', 'Technical error during photo selection:', err);
    return { success: false, cancelled: false, error: `Impossible de sélectionner la photo: ${err?.message || 'erreur inconnue'}` };
  }
};

/**
 * Fallback pour sélectionner une photo sur le web
 */
const pickPhotoFromLibraryWeb = (): Promise<PhotoResult> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        resolve({ success: false, cancelled: true });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          resolve({ success: true, photo: result });
        } else {
          resolve({ success: false, cancelled: false, error: 'Erreur lors de la lecture du fichier' });
        }
      };
      reader.onerror = () => {
        console.error('❌ Erreur lors de la lecture du fichier');
        resolve({ success: false, cancelled: false, error: 'Erreur lors de la lecture du fichier' });
      };
      reader.readAsDataURL(file);
    };

    input.oncancel = () => {
      resolve({ success: false, cancelled: true });
    };

    input.click();
  });
};

/**
 * Prend une photo avec la caméra de l'appareil
 * @param options Options pour la capture photo
 * @returns PhotoResult avec le résultat de l'opération
 */
export const takePhoto = async (options?: {
  quality?: number;
  allowEditing?: boolean;
  width?: number;
  height?: number;
}): Promise<PhotoResult> => {
  const platform = Capacitor.getPlatform();
  logDebug('📸 [takePhoto]', `Starting camera on platform: ${platform}`);
  
  try {
    if (Capacitor.isNativePlatform()) {
      // Vérifier et demander les permissions d'abord
      logDebug('📸 [takePhoto]', 'Checking camera permissions...');
      const hasPermission = await checkCameraPermissions();
      
      if (!hasPermission) {
        logDebug('📸 [takePhoto]', 'Permission not granted, requesting...');
        const granted = await requestCameraPermissions();
        
        if (!granted) {
          logDebug('❌ [takePhoto]', 'Camera permission denied by user');
          return { 
            success: false, 
            cancelled: false, 
            error: 'Permission refusée. Veuillez autoriser l\'accès à la caméra dans les réglages.' 
          };
        }
      }
      
      logDebug('📸 [takePhoto]', 'Opening native camera...', {
        quality: options?.quality || 90,
        allowEditing: options?.allowEditing || false,
        width: options?.width,
        height: options?.height,
      });
      
      const photo = await Camera.getPhoto({
        quality: options?.quality || 90,
        allowEditing: options?.allowEditing || false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: options?.width,
        height: options?.height,
        correctOrientation: true,
      });

      logDebug('📸 [takePhoto]', 'Photo received:', {
        format: photo.format,
        hasDataUrl: !!photo.dataUrl,
        hasWebPath: !!photo.webPath,
        dataUrlLength: photo.dataUrl?.length,
      });

      const photoUrl = photo.dataUrl || photo.webPath || null;
      if (photoUrl) {
        logDebug('✅ [takePhoto]', `Photo captured successfully, format: ${photo.format}`);
        return { success: true, photo: photoUrl };
      } else {
        logDebug('⚠️ [takePhoto]', 'No photo URL returned');
        return { success: false, cancelled: true };
      }
    } else {
      // Fallback pour le web
      logDebug('📸 [takePhoto]', 'Opening web capture input...');
      return await takePhotoWeb();
    }
  } catch (err: any) {
    logDebug('📸 [takePhoto]', 'Error or cancellation:', {
      message: err?.message,
      code: err?.code,
      stack: err?.stack,
    });
    
    if (isUserCancellation(err)) {
      logDebug('📸 [takePhoto]', 'Operation cancelled by user');
      return { success: false, cancelled: true };
    }
    
    if (isPermissionDenied(err)) {
      logDebug('❌ [takePhoto]', 'Permission denied for camera');
      return { 
        success: false, 
        cancelled: false, 
        error: 'Permission refusée. Veuillez autoriser l\'accès à la caméra dans les réglages.' 
      };
    }
    
    logDebug('❌ [takePhoto]', 'Technical error during photo capture:', err);
    return { success: false, cancelled: false, error: `Impossible d'accéder à la caméra: ${err?.message || 'erreur inconnue'}` };
  }
};

/**
 * Fallback pour prendre une photo sur le web
 */
const takePhotoWeb = (): Promise<PhotoResult> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    (input as any).capture = 'environment';
    
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        resolve({ success: false, cancelled: true });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          resolve({ success: true, photo: result });
        } else {
          resolve({ success: false, cancelled: false, error: 'Erreur lors de la lecture du fichier' });
        }
      };
      reader.onerror = () => {
        console.error('❌ Erreur lors de la lecture du fichier');
        resolve({ success: false, cancelled: false, error: 'Erreur lors de la lecture du fichier' });
      };
      reader.readAsDataURL(file);
    };

    input.oncancel = () => {
      resolve({ success: false, cancelled: true });
    };

    input.click();
  });
};

/**
 * Sélectionne plusieurs photos depuis la bibliothèque
 * Note: Cette fonctionnalité nécessite un plugin supplémentaire ou une implémentation custom
 */
export const pickMultiplePhotosFromLibrary = async (): Promise<string[]> => {
  console.warn('⚠️ La sélection multiple n\'est pas encore implémentée');
  
  // Pour le web, on peut utiliser l'attribut multiple
  if (!Capacitor.isNativePlatform()) {
    return await pickMultiplePhotosFromLibraryWeb();
  }
  
  // Pour le natif, il faudrait utiliser un plugin comme @capacitor-community/media
  return [];
};

/**
 * Fallback pour sélectionner plusieurs photos sur le web
 */
const pickMultiplePhotosFromLibraryWeb = (): Promise<string[]> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = async (event) => {
      const files = Array.from((event.target as HTMLInputElement).files || []);
      if (files.length === 0) {
        resolve([]);
        return;
      }

      const results: string[] = [];
      for (const file of files) {
        try {
          const dataUrl = await readFileAsDataUrl(file);
          if (dataUrl) {
            results.push(dataUrl);
          }
        } catch (err) {
          console.error('❌ Erreur lors de la lecture du fichier:', err);
        }
      }
      
      resolve(results);
    };

    input.oncancel = () => {
      resolve([]);
    };

    input.click();
  });
};

/**
 * Lit un fichier et retourne son contenu en Data URL
 */
const readFileAsDataUrl = (file: File): Promise<string | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target?.result as string || null);
    };
    reader.onerror = () => {
      resolve(null);
    };
    reader.readAsDataURL(file);
  });
};
